<template>
  <!-- New Hike List inside the Save to list dialog: My Lists' fields (Name, Description,
       Visibility, Add Photos), with "Create and save" adding the hike in the same step.
       New lists start as Only you. -->
  <div class="stl-new">
    <div class="stl-handle" aria-hidden="true"></div>
    <div class="stl-head">
      <button class="stl-icon-btn" type="button" :aria-label="$t('save_to_list.back_to_lists')" data-test="new-list-back" @click="$emit('back')">
        <span class="ms" aria-hidden="true">arrow_back</span>
      </button>
      <div class="stl-grow">
        <h2 id="saveToListTitle" class="stl-title">{{ $t('save_to_list.new_hike_list') }}</h2>
        <div class="stl-subtitle">
          {{ hikeName ? $t('save_to_list.will_be_saved_to_it', { name: hikeName }) : $t('save_to_list.this_hike_will_be_saved_to_it') }}
        </div>
      </div>
    </div>

    <form class="stl-body stl-form" novalidate @submit.prevent="submit">
      <div v-if="error" class="stl-error" role="alert" data-test="new-list-error">{{ error }}</div>
      <label class="stl-flabel" for="saveToListName">{{ $t('save_to_list.name') }}</label>
      <input
        id="saveToListName"
        ref="name"
        v-model="name"
        class="stl-field"
        :placeholder="$t('save_to_list.list_name')"
        autocomplete="off"
        maxlength="255"
        data-test="new-list-name"
      >
      <label class="stl-flabel" for="saveToListDescription">{{ $t('save_to_list.description') }}</label>
      <textarea
        id="saveToListDescription"
        v-model="description"
        class="stl-field"
        :placeholder="$t('save_to_list.description_placeholder')"
        data-test="new-list-description"
      ></textarea>

      <span class="stl-flabel">{{ $t('save_to_list.visibility') }}</span>
      <div class="stl-vis-row" role="radiogroup" :aria-label="$t('save_to_list.visibility')">
        <button
          class="stl-vis"
          :class="{ on: !publiclyVisible }"
          type="button"
          role="radio"
          :aria-checked="!publiclyVisible"
          data-test="new-list-private"
          @click="publiclyVisible = false"
        >
          <span class="ms fill" aria-hidden="true">lock</span>
          <span>{{ $t('save_to_list.only_you') }}<span class="sub">{{ $t('save_to_list.just_for_you') }}</span></span>
        </button>
        <button
          class="stl-vis"
          :class="{ on: publiclyVisible }"
          type="button"
          role="radio"
          :aria-checked="publiclyVisible"
          data-test="new-list-public"
          @click="publiclyVisible = true"
        >
          <span class="ms" aria-hidden="true">public</span>
          <span>{{ $t('save_to_list.everyone') }}<span class="sub">{{ $t('save_to_list.on_your_profile') }}</span></span>
        </button>
      </div>

      <span class="stl-flabel">{{ $t('save_to_list.add_photos') }}</span>
      <div class="stl-photos">
        <div v-for="(photo, index) in photos" :key="photo.key" class="stl-photo" data-test="new-list-photo">
          <img :src="photo.preview" alt="">
          <button
            class="stl-photo-remove"
            type="button"
            :aria-label="$t('save_to_list.remove_photo')"
            @click="removePhoto(index)"
          ><span class="ms" aria-hidden="true">close</span></button>
        </div>
        <label class="stl-addphoto">
          <input type="file" accept="image/*" multiple class="stl-file" data-test="new-list-photos-input" @change="addPhotos">
          <span class="ms" aria-hidden="true">add_photo_alternate</span>{{ $t('save_to_list.add_photos') }}
        </label>
      </div>
    </form>

    <div class="stl-foot">
      <button class="stl-btn stl-btn-lg stl-btn-secondary" type="button" data-test="new-list-cancel" @click="$emit('back')">
        {{ $t('save_to_list.cancel') }}
      </button>
      <button
        class="stl-btn stl-btn-lg stl-btn-primary"
        type="button"
        :disabled="!canCreate"
        data-test="new-list-create"
        @click="submit"
      >{{ $t('save_to_list.create_and_save') }}</button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  props: {
    hikeName: { type: String, default: '' },
    // What was typed in the search becomes the name.
    initialName: { type: String, default: '' },
    busy: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  emits: ['back', 'create'],
  data() {
    return {
      name: this.initialName,
      description: '',
      publiclyVisible: false,
      photos: [],
      nextPhotoKey: 0,
    };
  },
  computed: {
    canCreate() {
      return this.name.trim() !== '' && !this.busy;
    },
  },
  mounted() {
    this.$refs.name?.focus();
  },
  beforeUnmount() {
    this.photos.forEach((photo) => URL.revokeObjectURL?.(photo.preview));
  },
  methods: {
    addPhotos(event) {
      const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith('image/'));
      files.forEach((file) => {
        this.nextPhotoKey += 1;
        this.photos.push({ key: this.nextPhotoKey, file, preview: URL.createObjectURL?.(file) || '' });
      });
      event.target.value = '';
    },
    removePhoto(index) {
      const [photo] = this.photos.splice(index, 1);
      if (photo) URL.revokeObjectURL?.(photo.preview);
    },
    submit() {
      if (!this.canCreate) return;
      this.$emit('create', {
        name: this.name.trim(),
        description: this.description.trim(),
        publiclyVisible: this.publiclyVisible,
        images: this.photos.map((photo) => photo.file),
      });
    },
  },
});
</script>

<style scoped>
.stl-new {
  --w-modal: #1c1e22;
  --w-gray-dark: #1f2026;
  --w-gray: #22242a;
  --w-border: #434343;
  --w-hair: rgba(255, 255, 255, 0.08);
  --w-muted: #b2b2b2;
  --w-placeholder: #898989;
  --w-yellow: #fddf00;
  --w-yellow-ink: #36220f;
  --w-red-soft: #ff6b73;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  color: #fff;
  font: 400 14px/1.4 Montserrat, sans-serif;
  text-align: left;
}

.ms {
  font-family: 'Material Symbols Outlined';
  font-weight: 400;
  font-style: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
.ms.fill { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

.stl-handle { display: none; }
.stl-head { display: flex; align-items: center; gap: 12px; padding: 18px 18px 12px; flex: none; }
.stl-grow { flex: 1; min-width: 0; }
.stl-title { font: 700 17px/1.25 Montserrat, sans-serif; margin: 0; color: #fff; }
.stl-subtitle { font: 400 13px Montserrat, sans-serif; color: var(--w-muted); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stl-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: none;
  padding: 0;
}
.stl-icon-btn:hover { background: rgba(255, 255, 255, 0.06); }

.stl-body {
  overflow-y: auto;
  padding: 14px 18px 18px;
  display: grid;
  /* Rows keep their content's height, so a short window scrolls the form instead of
     squeezing the description box (a scroll container, so its minimum height is 0). */
  grid-auto-rows: max-content;
  gap: 12px;
  align-content: start;
  min-height: 0;
  flex: 1;
  margin: 0;
}
.stl-error { background: rgba(219, 43, 53, 0.14); color: var(--w-red-soft); border-radius: 10px; padding: 10px 12px; font: 500 13px Montserrat, sans-serif; }
.stl-flabel { font: 600 14px Montserrat, sans-serif; margin: 4px 2px -2px; }
.stl-field {
  background: var(--w-gray-dark);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #fff;
  font: 400 14px Montserrat, sans-serif;
  padding: 0 12px;
  height: 44px;
  width: 100%;
}
.stl-field::placeholder { color: var(--w-placeholder); }
textarea.stl-field { height: auto; min-height: 88px; padding: 10px 12px; resize: none; line-height: 1.45; }

.stl-vis-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.stl-vis {
  background: var(--w-gray);
  border: 1.5px solid transparent;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  color: #fff;
  font: 500 14px Montserrat, sans-serif;
  text-align: left;
}
.stl-vis .ms { font-size: 18px; }
.stl-vis .sub { display: block; font: 400 12px Montserrat, sans-serif; color: var(--w-muted); }
.stl-vis.on { border-color: var(--w-yellow); }

.stl-photos { display: flex; flex-wrap: wrap; gap: 8px; }
.stl-photo { position: relative; width: 120px; height: 90px; border-radius: 8px; overflow: hidden; background: var(--w-gray); }
.stl-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.stl-photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.stl-photo-remove .ms { font-size: 16px; }
.stl-addphoto {
  position: relative;
  width: 120px;
  height: 90px;
  margin: 0;
  border: 1px dashed #5a5d63;
  border-radius: 8px;
  background: var(--w-gray);
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 4px;
  color: var(--w-muted);
  font: 500 12px Montserrat, sans-serif;
  cursor: pointer;
}
.stl-addphoto .ms { font-size: 24px; }
.stl-file { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }

.stl-foot {
  flex: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px 18px 16px;
  border-top: 1px solid var(--w-hair);
  background: var(--w-modal);
}
.stl-btn {
  border-radius: 100px;
  font: 600 14px Montserrat, sans-serif;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}
.stl-btn-lg { height: 47px; padding: 0 22px; }
.stl-btn-primary { background: var(--w-yellow); color: var(--w-yellow-ink); }
.stl-btn-secondary { background: var(--w-gray-dark); color: #fff; border-color: var(--w-border); }
.stl-btn-secondary:hover { background: #333; }
.stl-btn[disabled] { opacity: 0.45; cursor: default; }

.stl-btn:focus-visible,
.stl-icon-btn:focus-visible,
.stl-vis:focus-visible,
.stl-photo-remove:focus-visible,
.stl-addphoto:focus-within,
.stl-field:focus-visible { outline: 2px solid var(--w-yellow); outline-offset: 2px; }

@media (max-width: 575.98px) {
  .stl-handle { display: block; width: 36px; height: 4px; border-radius: 2px; background: #3a3c42; margin: 8px auto 0; flex: none; }
  .stl-head { padding-top: 10px; }
}
</style>
